<script setup lang="ts">
interface Props {
  imageLeft: boolean
  buttonText?: string
  imageSrc?: string
  videoSrc?: string
  imageAlt?: string
  customButtonClass?: string
  customWrapperClass?: string
  customImageClass?: string
  scrollToMainContent: Function
  galleryImages?: []
}
const { imageLeft, buttonText, imageSrc, videoSrc, imageAlt, customButtonClass, customWrapperClass, customImageClass, scrollToMainContent, gallery, galleryImages } = defineProps<Props>()

const runtimeConfig = useRuntimeConfig()

let observer = ref()
const videoSources = computed(() => {
const baseUrl = `${runtimeConfig.public.cdn}/static/dla-firm`
const videoBaseName = videoSrc.split('/').pop()

  return [
    { url: `${baseUrl}/${videoBaseName}.mp4`, type: 'video/mp4', media: '(min-width: 1920px)' },
    { url: `${baseUrl}/${videoBaseName}-720.mp4`, type: 'video/mp4', media: '(min-width: 1280px)' },
    { url: `${baseUrl}/${videoBaseName}-480.mp4`, type: 'video/mp4', media: '(min-width: 800px)' },
    { url: `${baseUrl}/${videoBaseName}-360.mp4`, type: 'video/mp4' }
  ]
})
onMounted(() => {
  initIntersectionObserver()
})
onBeforeUnmount(() => {
  if (observer.value) {
    observer.value.disconnect()
  }
})
const initIntersectionObserver = () => {
  observer.value = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.target.tagName === 'VIDEO') {
          entry.target.play()
        } else if (entry.target.tagName === 'VIDEO') {
          entry.target.pause()
        }
      })
    },
    { threshold: 0.5 }
  )

  const videos = document.querySelectorAll('video')
  videos.forEach(video => observer.value.observe(video))
}
const scrollToSection = () => {
  scrollToMainContent()
}
</script>

<template>
  <div :class="[imageLeft ? 'lg:flex-row-reverse' : 'lg:flex-row', 'flex-col flex items-center xl:my-16 xl:px-16 2xl:my-32 2xl:px-32 xl:py-8 px-6 py-4', customWrapperClass]">
    <div :class="[imageLeft ? 'lg:ml-16 2xl:pl-8' : 'lg:mr-16 2xl:mr-32', 'w-full lg:w-1/2']">
      <h2 class="font-bold leading-8 text-24 md:text-[30px] md:leading-10 xl:text-[36px] xl:leading-[44px] 2xl:text-[50px] 2xl:leading-[63px]">
        <slot name="title" />
      </h2>
      <div class="flex flex-col gap-4 mt-4">
        <slot />
      </div>
      <a
        v-if="buttonText"
        :class="[customButtonClass, 'border font-bold transition-colors duration-200 mx-auto px-8 py-4 mt-4 inline-block', customButtonClass ? '' : 'border-red text-red hover:text-white hover:bg-red']"
        @click.prevent="scrollToSection"
      >
        {{ buttonText }}
      </a>
    </div>
    <div class="w-full my-4 xl:m-0 lg:w-1/2">
      <Carousel v-if="galleryImages?.length" :options="{ loop: true }" class="relative">
        <Carousel.Slide v-for="(image, imageIndex) in galleryImages" :key="imageIndex" class="shrink-0 basis-full">
          <Img :src="image.url" width="800" height="450" :alt="image.alt" :loading="imageIndex === 0 ? 'eager' : 'lazy'" />
        </Carousel.Slide>

        <template #addons="{ prev, next }">
          <button class="z-10 text-16 bg-white/60 absolute w-10 h-10 top-1/2 -translate-y-1/2 cursor-pointer rounded flex items-center justify-center left-2" @click.stop="prev">
            <i class="icon-arrow-button -rotate-180" />
          </button>

          <button class="z-10 text-16 bg-white/60 absolute w-10 h-10 top-1/2 -translate-y-1/2 cursor-pointer rounded flex items-center justify-center right-2" @click.stop="next">
            <i class="icon-arrow-button" />
          </button>
        </template>
      </Carousel>
      <Img
        v-else-if="imageSrc"
        :src="imageSrc"
        :alt="imageAlt"
        width="800"
        height="450"
        :class="['w-full h-auto', customImageClass]"
      />
      <video
        v-else-if="videoSrc"
        ref="video"
        :src="null"
        :autoplay="false"
        :muted="true"
        :playsinline="true"
        :loop="true"
        :class="['w-full h-auto', customImageClass]"
      >
        <source v-for="(src, index) in videoSources" :key="index" :src="src.url" :type="src.type" :media="src.media">
      </video>
    </div>
  </div>
</template>
